import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { database } from '../../firebaseConfig';
import { ref, get } from 'firebase/database';
import '../Pages/BusinessAnalysisPreview.css';

function BusinessAnalysisPublic() {
    const { shareToken } = useParams();
    const navigate = useNavigate();
    const [analysisData, setAnalysisData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (shareToken) {
            loadPublicAnalysis();
        }
    }, [shareToken]);

    const loadPublicAnalysis = async () => {
        try {
            console.log(`Loading public analysis with token: ${shareToken}`);
            
            const publicRef = ref(database, `publicBusinessAnalyses/${shareToken}`);
            const snapshot = await get(publicRef);
            
            if (snapshot.exists()) {
                const data = snapshot.val();
                console.log('Public analysis loaded:', data);
                setAnalysisData(data);
            } else {
                setError('Öffentliche Analyse nicht gefunden');
            }
        } catch (err) {
            console.error('Error loading public analysis:', err);
            setError('Fehler beim Laden der Analyse');
        }
        setIsLoading(false);
    };

    // Copy all helper functions from BusinessAnalysisPreview
    const getMarketSaturationLabel = (value) => {
        switch (value) {
            case 'under100': return '🟢 Weniger als 100 Konkurrenten';
            case '100-1000': return '🟡 100-1000 Konkurrenten';
            case 'over1000': return '🔴 Über 1000 Konkurrenten';
            default: return value;
        }
    };

    const getLanguageLabel = (value) => {
        const languages = {
            'german': '🇩🇪 Deutsch',
            'english': '🇺🇸 Englisch',
            'spanish': '🇪🇸 Spanisch',
            'french': '🇫🇷 Französisch',
            'italian': '🇮🇹 Italienisch'
        };
        return languages[value] || value;
    };

    const getSliderDescription = (value) => {
        if (value <= 3) return 'Verbesserungsbedarf';
        if (value <= 6) return 'Durchschnittlich';
        if (value <= 8) return 'Gut';
        return 'Exzellent';
    };

    const getSourceLabel = (source) => {
        const sources = {
            'personal': 'Persönliches Netzwerk',
            'organic-instagram': 'Organisch Instagram',
            'organic-tiktok': 'Organisch TikTok',
            'organic-youtube': 'Organisch YouTube',
            'organic-facebook': 'Organisch Facebook',
            'organic-linkedin': 'Organisch LinkedIn',
            'seo': 'SEO / Google Search',
            'podcast': 'Podcast',
            'content-marketing': 'Content Marketing',
            'referrals': 'Empfehlungen',
            'community': 'Community',
            'meta-ads': 'Meta Ads',
            'google-ads': 'Google Ads',
            'youtube-ads': 'YouTube Ads',
            'tiktok-ads': 'TikTok Ads',
            'linkedin-ads': 'LinkedIn Ads',
            'pinterest-ads': 'Pinterest Ads',
            'twitter-ads': 'Twitter Ads',
            'spotify-ads': 'Spotify Ads',
            'cold-email': 'Cold Email',
            'cold-calling': 'Cold Calling',
            'live-events': 'Live Events',
            'webinars': 'Webinare',
            'partnerships': 'Partnerschaften',
            'affiliate': 'Affiliate Marketing',
            'newsletter': 'Newsletter',
            'ads': 'Bezahlte Werbung'
        };
        return sources[source] || source;
    };

    const getProofSourceLabel = (source) => {
        const sources = {
            'instagram': 'Instagram',
            'google': 'Google Reviews',
            'google-visibility': 'Google Visibility',
            'facebook': 'Facebook',
            'linkedin': 'LinkedIn',
            'testimonials': 'Kundenstimmen',
            'press': 'Presse & Medien',
            'awards': 'Awards & Zertifikate',
            'community': 'Community Feedback',
            'trustpilot': 'Trustpilot',
            'youtube': 'YouTube',
            'podcast': 'Podcast Erwähnungen'
        };
        return sources[source] || source;
    };

    const getPlatformLabel = (platform) => {
        const platforms = {
            'instagram': 'Instagram',
            'facebook': 'Facebook',
            'youtube': 'YouTube',
            'tiktok': 'TikTok',
            'linkedin': 'LinkedIn',
            'pinterest': 'Pinterest',
            'twitter_x': 'Twitter / X',
            'podcast': 'Podcast',
            'newsletter': 'Newsletter'
        };
        return platforms[platform] || platform;
    };

    const getPlatformIcon = (platform) => {
        const icons = {
            'instagram': 'fab fa-instagram',
            'facebook': 'fab fa-facebook',
            'youtube': 'fab fa-youtube',
            'tiktok': 'fab fa-tiktok',
            'linkedin': 'fab fa-linkedin',
            'pinterest': 'fab fa-pinterest',
            'twitter_x': 'fab fa-twitter',
            'podcast': 'fas fa-podcast',
            'newsletter': 'fas fa-envelope'
        };
        return icons[platform] || 'fas fa-globe';
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num?.toString() || '0';
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#00ff88';
        if (score >= 65) return '#e91e63';
        if (score >= 50) return '#ffd93d';
        return '#ff6b9d';
    };

    // Calculate Business Strength (same logic as Preview)
    const calculateBusinessStrength = () => {
        if (!analysisData) return null;

        const branding = analysisData.branding || {};
        const brandingScore = ((branding.brandingQuality || 5) + 
                              (branding.messageDesignAlignment || 5) + 
                              (branding.photoQuality || 5) + 
                              (branding.videoMaterial || 5)) / 4 * 10;
        
        const leadCount = analysisData.leadSources?.length || 0;
        const leadAvg = leadCount > 0 
            ? analysisData.leadSources.reduce((sum, s) => sum + (s.strength || 5), 0) / leadCount 
            : 5;
        const leadScore = Math.min((leadCount * 15) + (leadAvg * 5), 100);
        
        const productCount = analysisData.products?.filter(p => p.name && p.name.trim().length > 0).length || 0;
        const hasHighTicket = analysisData.products?.some(p => ['500-1000', '1000-5000', '5000-10000', '10000-20000', '20000-50000', 'over50000'].includes(p.cost)) || false;
        const hasRecurring = analysisData.products?.some(p => p.paymentType === 'monthly') || false;
        const productScore = Math.min((productCount * 15) + (hasHighTicket ? 25 : 0) + (hasRecurring ? 30 : 0), 100);
        
        const proofCount = analysisData.socialProof?.length || 0;
        const proofAvg = proofCount > 0 
            ? analysisData.socialProof.reduce((sum, p) => sum + (p.strength || 5), 0) / proofCount 
            : 5;
        const proofScore = Math.min((proofCount * 12) + (proofAvg * 6), 100);
        
        const overallScore = Math.round(
            (brandingScore * 0.30) +
            (leadScore * 0.30) +
            (productScore * 0.20) +
            (proofScore * 0.20)
        );
        
        let rating = '';
        if (overallScore >= 80) rating = '🟢 Exzellent';
        else if (overallScore >= 65) rating = '🔵 Gut';
        else if (overallScore >= 50) rating = '🟡 Durchschnittlich';
        else if (overallScore >= 35) rating = '🟠 Ausbaufähig';
        else rating = '🔴 Kritisch';

        // Calculate total followers from lead sources
        const totalFollowers = analysisData.leadSources?.reduce((sum, source) => {
            const count = parseInt(source.followerCount) || 0;
            return sum + count;
        }, 0) || 0;

        const socialProofCount = analysisData.socialProof?.length || 0;

        return {
            scores: {
                overall: overallScore,
                branding: Math.round(brandingScore),
                leadSources: Math.round(leadScore),
                products: Math.round(productScore),
                socialProof: Math.round(proofScore)
            },
            rating,
            metrics: {
                leadCount,
                productCount,
                totalFollowers,
                socialProofCount
            }
        };
    };

    if (isLoading) {
        return (
            <div className="business-analyse-preview-loading">
                <div className="business-analyse-preview-spinner"></div>
                <p>Lade öffentliche Analyse...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="business-analyse-preview-error">
                <div className="business-analyse-preview-error-content">
                    <i className="fas fa-exclamation-triangle"></i>
                    <h2>{error}</h2>
                    <p>Diese Analyse ist nicht öffentlich verfügbar oder wurde gelöscht.</p>
                </div>
            </div>
        );
    }

    if (!analysisData) {
        return null;
    }

    const businessAnalysis = calculateBusinessStrength();

    return (
        <div className="business-analyse-preview business-analyse-public">
            {/* Public Badge */}
            <div className="business-analyse-public-badge">
                <i className="fas fa-globe"></i>
                Öffentliche Analyse
            </div>

            {/* Header */}
            <div className="business-analyse-preview-header">
                <div className="business-analyse-preview-header-content">
                    <div className="business-analyse-preview-title-section">
                        <div>
                            <h1 className="business-analyse-preview-title">{analysisData.name}</h1>
                            <div className="business-analyse-preview-meta">
                                <span className="business-analyse-preview-brand-type">
                                    <i className="fas fa-building"></i>
                                    {analysisData.brandType === 'personal' ? 'Personal Brand' : 'Corporate Brand'}
                                </span>
                                <span className="business-analyse-preview-date">
                                    <i className="fas fa-share-alt"></i>
                                    Geteilt am {new Date(analysisData.publishedAt || analysisData.sharedAt || new Date()).toLocaleDateString('de-DE')}
                                </span>
                            </div>
                        </div>
                        <button 
                            className="business-analyse-copy-link-btn"
                            onClick={() => {
                                const url = window.location.href;
                                navigator.clipboard.writeText(url).then(() => {
                                    alert('Link kopiert! Du kannst ihn jetzt teilen.');
                                });
                            }}
                        >
                            <i className="fas fa-link"></i>
                            Link kopieren
                        </button>
                    </div>
                </div>
            </div>

            <div className="business-analyse-preview-container">
                {/* Business Score Overview - Same as private preview */}
                {businessAnalysis && (
                    <div className="business-analyse-overview-hero">
                        <div className="business-analyse-overview-score-section">
                            <div className="business-analyse-overview-score-circle">
                                <svg width="180" height="180" viewBox="0 0 180 180">
                                    <circle cx="90" cy="90" r="80" fill="none" stroke="rgba(226, 232, 240, 0.3)" strokeWidth="12" />
                                    <circle
                                        cx="90" cy="90" r="80" fill="none"
                                        stroke={getScoreColor(businessAnalysis.scores.overall)}
                                        strokeWidth="12"
                                        strokeDasharray={`${(businessAnalysis.scores.overall / 100) * 502.4} 502.4`}
                                        strokeLinecap="round"
                                        transform="rotate(-90 90 90)"
                                    />
                                </svg>
                                <div className="business-analyse-overview-score-content">
                                    <span className="business-analyse-overview-score-number">{businessAnalysis.scores.overall}</span>
                                    <span className="business-analyse-overview-score-label">Score</span>
                                </div>
                            </div>
                            <div className="business-analyse-overview-score-info">
                                <h2 className="business-analyse-overview-rating">{businessAnalysis.rating}</h2>
                                <p className="business-analyse-overview-description">
                                    Umfassende Business-Analyse mit detaillierten Metriken und Insights.
                                </p>
                            </div>
                        </div>

                        {/* Quick Metrics */}
                        <div className="business-analyse-overview-quick-metrics">
                            <div className="business-analyse-quick-metric">
                                <i className="fas fa-funnel-dollar"></i>
                                <div>
                                    <span className="business-analyse-quick-metric-value">{businessAnalysis.metrics.leadCount}</span>
                                    <span className="business-analyse-quick-metric-label">Lead-Quellen</span>
                                </div>
                            </div>
                            <div className="business-analyse-quick-metric">
                                <i className="fas fa-box"></i>
                                <div>
                                    <span className="business-analyse-quick-metric-value">{businessAnalysis.metrics.productCount}</span>
                                    <span className="business-analyse-quick-metric-label">Produkte</span>
                                </div>
                            </div>
                            <div className="business-analyse-quick-metric">
                                <i className="fas fa-users"></i>
                                <div>
                                    <span className="business-analyse-quick-metric-value">{formatNumber(businessAnalysis.metrics.totalFollowers)}</span>
                                    <span className="business-analyse-quick-metric-label">Gesamt Follower</span>
                                </div>
                            </div>
                            <div className="business-analyse-quick-metric">
                                <i className="fas fa-certificate"></i>
                                <div>
                                    <span className="business-analyse-quick-metric-value">{businessAnalysis.metrics.socialProofCount}</span>
                                    <span className="business-analyse-quick-metric-label">Social Proof</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Category Scores */}
                {businessAnalysis && (
                    <div className="business-analyse-preview-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-chart-bar"></i>
                                Score-Übersicht
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            <div className="business-analyse-category-scores">
                                <div className="business-analyse-category-score-item">
                                    <div className="business-analyse-category-score-header">
                                        <div>
                                            <i className="fas fa-palette"></i>
                                            <span>Branding</span>
                                        </div>
                                        <strong>{businessAnalysis.scores.branding}/100</strong>
                                    </div>
                                    <div className="business-analyse-category-score-bar">
                                        <div 
                                            className="business-analyse-category-score-fill"
                                            style={{ 
                                                width: `${businessAnalysis.scores.branding}%`,
                                                background: getScoreColor(businessAnalysis.scores.branding)
                                            }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="business-analyse-category-score-item">
                                    <div className="business-analyse-category-score-header">
                                        <div>
                                            <i className="fas fa-funnel-dollar"></i>
                                            <span>Lead-Quellen</span>
                                        </div>
                                        <strong>{businessAnalysis.scores.leadSources}/100</strong>
                                    </div>
                                    <div className="business-analyse-category-score-bar">
                                        <div 
                                            className="business-analyse-category-score-fill"
                                            style={{ 
                                                width: `${businessAnalysis.scores.leadSources}%`,
                                                background: getScoreColor(businessAnalysis.scores.leadSources)
                                            }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="business-analyse-category-score-item">
                                    <div className="business-analyse-category-score-header">
                                        <div>
                                            <i className="fas fa-box"></i>
                                            <span>Produkt-Portfolio</span>
                                        </div>
                                        <strong>{businessAnalysis.scores.products}/100</strong>
                                    </div>
                                    <div className="business-analyse-category-score-bar">
                                        <div 
                                            className="business-analyse-category-score-fill"
                                            style={{ 
                                                width: `${businessAnalysis.scores.products}%`,
                                                background: getScoreColor(businessAnalysis.scores.products)
                                            }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="business-analyse-category-score-item">
                                    <div className="business-analyse-category-score-header">
                                        <div>
                                            <i className="fas fa-certificate"></i>
                                            <span>Social Proof</span>
                                        </div>
                                        <strong>{businessAnalysis.scores.socialProof}/100</strong>
                                    </div>
                                    <div className="business-analyse-category-score-bar">
                                        <div 
                                            className="business-analyse-category-score-fill"
                                            style={{ 
                                                width: `${businessAnalysis.scores.socialProof}%`,
                                                background: getScoreColor(businessAnalysis.scores.socialProof)
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Grunddaten Section */}
                <div className="business-analyse-preview-section">
                    <div className="business-analyse-preview-section-header">
                        <h2>
                            <i className="fas fa-info-circle"></i>
                            Grunddaten
                        </h2>
                    </div>
                    <div className="business-analyse-preview-content">
                        <div className="business-analyse-preview-grid">
                            <div className="business-analyse-preview-field">
                                <label>Nische</label>
                                <div className="business-analyse-preview-value">
                                    {analysisData.niche || 'Nicht angegeben'}
                                </div>
                            </div>
                            <div className="business-analyse-preview-field">
                                <label>Marktsättigung</label>
                                <div className="business-analyse-preview-value">
                                    {getMarketSaturationLabel(analysisData.marketSaturation)}
                                </div>
                            </div>
                            <div className="business-analyse-preview-field">
                                <label>Geografischer Fokus</label>
                                <div className="business-analyse-preview-value">
                                    {analysisData.geoFocus || 'Nicht angegeben'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Branding Section */}
                {analysisData.branding && (
                    <div className="business-analyse-preview-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-palette"></i>
                                Markenimage & Branding
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            {analysisData.branding.embodiment && (
                                <div className="business-analyse-preview-field-full">
                                    <label>Was verkörpert die Marke?</label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.embodiment}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.emotionalTriggers && (
                                <div className="business-analyse-preview-field-full">
                                    <label>Emotionale Trigger</label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.emotionalTriggers}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.design && (
                                <div className="business-analyse-preview-field-full">
                                    <label>
                                        <i className="fas fa-palette"></i>
                                        Design & Farbpsychologie
                                    </label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.design}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.wording && (
                                <div className="business-analyse-preview-field-full">
                                    <label>
                                        <i className="fas fa-comment-dots"></i>
                                        Wording & Tonalität
                                    </label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.wording}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.values && (
                                <div className="business-analyse-preview-field-full">
                                    <label>
                                        <i className="fas fa-gem"></i>
                                        Kernwerte
                                    </label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.values}
                                    </div>
                                </div>
                            )}
                            {analysisData.branding.hiddenPsychology && (
                                <div className="business-analyse-preview-field-full">
                                    <label>
                                        <i className="fas fa-user-secret"></i>
                                        Versteckte Psychologie
                                    </label>
                                    <div className="business-analyse-preview-value">
                                        {analysisData.branding.hiddenPsychology}
                                    </div>
                                </div>
                            )}
                            
                            <div className="business-analyse-preview-ratings">
                                <div className="business-analyse-preview-rating">
                                    <label>Branding-Qualität</label>
                                    <div className="business-analyse-preview-rating-bar">
                                        <div className="business-analyse-preview-rating-fill" style={{ width: `${(analysisData.branding.brandingQuality / 10) * 100}%` }}></div>
                                        <span className="business-analyse-preview-rating-value">
                                            {analysisData.branding.brandingQuality}/10 - {getSliderDescription(analysisData.branding.brandingQuality)}
                                        </span>
                                    </div>
                                </div>
                                <div className="business-analyse-preview-rating">
                                    <label>Message-Design Übereinstimmung</label>
                                    <div className="business-analyse-preview-rating-bar">
                                        <div className="business-analyse-preview-rating-fill" style={{ width: `${(analysisData.branding.messageDesignAlignment / 10) * 100}%` }}></div>
                                        <span className="business-analyse-preview-rating-value">
                                            {analysisData.branding.messageDesignAlignment}/10 - {getSliderDescription(analysisData.branding.messageDesignAlignment)}
                                        </span>
                                    </div>
                                </div>
                                <div className="business-analyse-preview-rating">
                                    <label>Foto-Qualität</label>
                                    <div className="business-analyse-preview-rating-bar">
                                        <div className="business-analyse-preview-rating-fill" style={{ width: `${(analysisData.branding.photoQuality / 10) * 100}%` }}></div>
                                        <span className="business-analyse-preview-rating-value">
                                            {analysisData.branding.photoQuality}/10 - {getSliderDescription(analysisData.branding.photoQuality)}
                                        </span>
                                    </div>
                                </div>
                                <div className="business-analyse-preview-rating">
                                    <label>Video-Material Qualität</label>
                                    <div className="business-analyse-preview-rating-bar">
                                        <div className="business-analyse-preview-rating-fill" style={{ width: `${(analysisData.branding.videoMaterial / 10) * 100}%` }}></div>
                                        <span className="business-analyse-preview-rating-value">
                                            {analysisData.branding.videoMaterial}/10 - {getSliderDescription(analysisData.branding.videoMaterial)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Lead Sources Section */}
                {analysisData.leadSources && analysisData.leadSources.length > 0 && (
                    <div className="business-analyse-preview-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-funnel-dollar"></i>
                                Lead-Quellen ({analysisData.leadSources.length})
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            <div className="business-analyse-preview-lead-sources">
                                {analysisData.leadSources.map((source, index) => (
                                    <div key={source.id || index} className="business-analyse-preview-lead-source">
                                        <div className="business-analyse-preview-lead-source-header">
                                            <h4>{getSourceLabel(source.source)}</h4>
                                            <div className="business-analyse-preview-strength">
                                                <span>Stärke: {source.strength}/10</span>
                                                <div className="business-analyse-preview-strength-bar">
                                                    <div 
                                                        className="business-analyse-preview-strength-fill"
                                                        style={{ width: `${(source.strength / 10) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                        {source.notes && (
                                            <div className="business-analyse-preview-lead-notes">
                                                <i className="fas fa-sticky-note"></i>
                                                {source.notes}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Products Section */}
                {analysisData.products && analysisData.products.length > 0 && analysisData.products.some(p => p.name?.trim()) && (
                    <div className="business-analyse-preview-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-box"></i>
                                Produkte ({analysisData.products.filter(p => p.name?.trim()).length})
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            <div className="business-analyse-preview-products">
                                {analysisData.products.filter(p => p.name?.trim()).map((product, index) => (
                                    <div key={product.id || index} className="business-analyse-preview-product">
                                        <h4>{product.name}</h4>
                                        <div className="business-analyse-preview-product-details">
                                            <span className="business-analyse-preview-product-type">
                                                {product.type === 'physical' ? 'Physisches Produkt' :
                                                 product.type === 'digital' ? 'Digitales Produkt' :
                                                 product.type === 'mix' ? 'Mix aus beiden' : product.type}
                                            </span>
                                            <span className="business-analyse-preview-product-payment">
                                                {product.paymentType === 'monthly' ? 'Monatlich' : 'Einmalzahlung'}
                                            </span>
                                        </div>
                                        {product.additionalInfo && (
                                            <div className="business-analyse-preview-product-info">
                                                {product.additionalInfo}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Social Proof Section */}
                {analysisData.socialProof && analysisData.socialProof.length > 0 && (
                    <div className="business-analyse-preview-section">
                        <div className="business-analyse-preview-section-header">
                            <h2>
                                <i className="fas fa-certificate"></i>
                                Social Proof & Vertrauen ({analysisData.socialProof.length})
                            </h2>
                        </div>
                        <div className="business-analyse-preview-content">
                            <div className="business-analyse-preview-lead-sources">
                                {analysisData.socialProof.map((proof, index) => (
                                    <div key={proof.id || index} className="business-analyse-preview-lead-source">
                                        <div className="business-analyse-preview-lead-source-header">
                                            <h4>{getProofSourceLabel(proof.source)}</h4>
                                            <div className="business-analyse-preview-strength">
                                                <span>Vertrauensstärke: {proof.strength}/10</span>
                                                <div className="business-analyse-preview-strength-bar">
                                                    <div 
                                                        className="business-analyse-preview-strength-fill"
                                                        style={{ width: `${(proof.strength / 10) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                        {proof.notes && (
                                            <div className="business-analyse-preview-lead-notes">
                                                <i className="fas fa-sticky-note"></i>
                                                {proof.notes}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* CTA Section - Encourage to create their own */}
                <div className="business-analyse-public-cta">
                    <i className="fas fa-rocket"></i>
                    <h3>Erstelle deine eigene Business-Analyse!</h3>
                    <p>Analysiere dein Business mit über 100 Datenpunkten und erhalte konkrete Empfehlungen.</p>
                    <button 
                        className="business-analyse-public-cta-btn"
                        onClick={() => navigate('/business-analysis')}
                    >
                        <i className="fas fa-chart-line"></i>
                        Jetzt starten
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BusinessAnalysisPublic;

